import { useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Button } from './Button'
import { CopyButton } from './CopyButton'
import s from './RefPopover.module.scss'

const WIDTH = 340
const MARGIN = 12

/**
 * Ab wann nach OBEN aufgeklappt wird.
 *
 * Dieselbe Schwelle wie bei `HelpIcon` und aus demselben Grund: Die Höhe steht
 * beim Messen noch nicht fest, sie wird erst danach gerendert. Statt zweimal
 * zu messen genügt eine Schwelle. Ein volles Popover mit Kurs, Ampel und drei
 * Meldungen wird gut 300 Pixel hoch.
 */
const FLIP_BELOW = 300

/** Eine Meldung im Schnipsel — Bewertung und Überschrift, mehr nicht. */
export interface RefNews {
  id: number
  /** −3 bis +3. Das Vorzeichen ist die Richtung, der Betrag die Stärke. */
  sentiment: number
  headline: string
}

/**
 * Was das Popover zu einem Verweis zeigt.
 *
 * ALLE FELDER AUSSER `ref` SIND FREIWILLIG, und das ist der Kern des Entwurfs:
 * Ein Instrument hat ISIN, Kurs und Detailseite, ein Land hat nichts davon,
 * ein toter Verweis hat nicht einmal einen Namen. Vier Erscheinungsformen aus
 * EINEM Typ — die Alternative wären vier Komponenten, die beim nächsten Feld
 * alle vier nachgezogen werden müssten.
 */
export interface RefDetail {
  /** Wie er gespeichert ist: `isin:US4581401001`, `geo:ISO3166:DE`. */
  ref: string
  /** isin, comm, ind, geo, crypt oder mkt. */
  kind: string | null
  /** Der Anzeigename. LEER heißt: Das Ziel gibt es nicht mehr. */
  name: string | null
  /** Die Art in Worten, für den Kopf: „Instrument", „Land · ISO 3166". */
  art?: string

  /** Der kopierbare Schlüssel. Beim Instrument die ISIN, sonst der Verweis. */
  schluessel?: { feld: string; wert: string }
  /** Weitere Eckdaten, je eine Zeile. */
  daten?: { feld: string; wert: string | null }[]

  kurs?: { betrag: string }
  ampel?: { stufe: 'GREEN' | 'AMBER' | 'RED'; seit: string; satz: string }
  news?: { anzahl: number; zuletzt: string; items: RefNews[] }

  /** Wohin man von hier aus springen kann. LEER heißt: nirgendwohin. */
  aktionen?: { text: string; to: string }[]
}

const STUFEN: Record<string, string> = {
  GREEN: 'Grün', AMBER: 'Gelb', RED: 'Rot',
}

interface Props {
  detail: RefDetail
  /** Was als Auslöser dasteht — in der Tabelle der Name des Ziels. */
  children: ReactNode
  /** Klasse für den Auslöser, damit die Zelle ihre Färbung behält. */
  className?: string
  /**
   * Meldet das Auf- und Zugehen nach außen.
   *
   * DAMIT ERST BEIM ÖFFNEN GELADEN WIRD: Das Popover selbst holt keine
   * Daten — es zeigt, was `detail` enthält. Wer nachlädt, erfährt hier, dass
   * es Zeit dafür ist. Eine Meldungsliste mit fünfzig Zeilen und drei Zielen
   * je Zeile stellte sonst hundertfünfzig Abrufe für Popover, die niemand
   * öffnet.
   */
  onOpenChange?: (offen: boolean) => void
}

/**
 * Ein Verweis, der sich aufklappen lässt.
 *
 * DER KLICK NAVIGIERT NICHT MEHR. Vorher führte ein Instrument direkt auf
 * seine Stammdaten, und alles andere war tot — ein Rohstoff, eine Branche,
 * ein Land ließen sich nicht anfassen. Jetzt klappt jeder Verweis auf, und
 * das Springen übernimmt ein Knopf im Popover. Das kostet einen Klick mehr
 * und bringt dafür zweierlei: Man sieht die Eckdaten, ohne die Seite zu
 * verlassen, und die vier Arten ohne Detailseite sind nicht länger stumm.
 *
 * NUR AUF KLICK, anders als `HelpIcon`, das auch auf Überfahren öffnet: Eine
 * Zeile trägt bis zu drei Ziele, und beim Überfahren ginge dauernd etwas auf.
 * Außerdem stehen hier Knöpfe, die angesteuert werden wollen — ein Popover,
 * das beim Verlassen des Auslösers zugeht, wäre nicht bedienbar.
 *
 * VERSCHIEBBAR AM KOPF. Das Popover geht dort auf, wo der Verweis steht,
 * und verdeckt damit die Zeilen darunter — genau die, die man daneben
 * lesen will. Wer den Kopf anfasst, zieht es weg; der Fensterrand bleibt
 * die Grenze. Beim Schließen vergisst es die Stelle: Das nächste Aufklappen
 * misst wieder am Verweis.
 */
export function RefPopover({ detail, children, className,
                            onOpenChange }: Props) {
  //: `top` ODER `bottom` — nie beides. Nach oben aufgeklappt wird am UNTEREN
  //: Rand verankert, damit das Popover nach oben wächst statt nach unten.
  const [place, setPlace] = useState<
    { left: number; top?: number; bottom?: number } | null>(null)
  const anchor = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  function zeigen(wohin: { left: number; top?: number; bottom?: number } | null) {
    setPlace(wohin)
    onOpenChange?.(wohin !== null)
  }

  function schliessen() {
    zeigen(null)
    anchor.current?.focus()
  }

  // Das Ziehen: Beim Anfassen wird die Lage in `top`/`left` festgehalten —
  // auch wenn das Popover nach oben aufgeklappt und am unteren Rand verankert
  // war —, danach folgt sie dem Zeiger. Knöpfe im Kopf bleiben Knöpfe.
  function ziehen(event: ReactPointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('button')) return
    const kasten = panel.current?.getBoundingClientRect()
    if (!kasten || event.button !== 0) return
    event.preventDefault()
    const startX = event.clientX, startY = event.clientY
    const startLeft = kasten.left, startTop = kasten.top
    const breite = kasten.width, hoehe = kasten.height
    function bewegen(e: PointerEvent) {
      const left = Math.max(MARGIN, Math.min(startLeft + e.clientX - startX,
                                             window.innerWidth - breite - MARGIN))
      const top = Math.max(MARGIN, Math.min(startTop + e.clientY - startY,
                                            window.innerHeight - hoehe - MARGIN))
      setPlace({ left, top })
    }
    function loslassen() {
      window.removeEventListener('pointermove', bewegen)
      window.removeEventListener('pointerup', loslassen)
      window.removeEventListener('pointercancel', loslassen)
    }
    window.addEventListener('pointermove', bewegen)
    window.addEventListener('pointerup', loslassen)
    window.addEventListener('pointercancel', loslassen)
  }

  function measure() {
    const box = anchor.current?.getBoundingClientRect()
    if (!box) return
    const left = Math.max(MARGIN,
      Math.min(box.left, window.innerWidth - WIDTH - MARGIN))
    const unten = window.innerHeight - box.bottom - MARGIN
    const oben = box.top - MARGIN
    zeigen(unten < FLIP_BELOW && oben > unten
      ? { left, bottom: window.innerHeight - box.top + 6 }
      : { left, top: box.bottom + 6 })
  }

  useEffect(() => {
    if (!place) return
    // DAS POPOVER SELBST MUSS AUSGENOMMEN SEIN, anders als bei `HelpIcon`:
    // Dort ist der Inhalt nur Text, hier stehen ein Kopierknopf und die
    // Sprünge darin. Ohne diese Prüfung schlösse der erste Klick auf einen
    // Knopf das Popover, bevor der Knopf ihn bekommt.
    function onAway(event: MouseEvent) {
      const ziel = event.target as Node
      if (anchor.current?.contains(ziel) || panel.current?.contains(ziel)) return
      zeigen(null)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') schliessen()
    }
    document.addEventListener('mousedown', onAway)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onAway)
      window.removeEventListener('keydown', onKey)
    }
  }, [place])

  // Beim Instrument steht die ISIN unter dem Namen, wie in der Vorlage — ein
  // Kennzeichen, kein Datenfeld. Der Kopierknopf haengt dran.
  const untertitel = detail.kind === 'isin' && detail.schluessel && (
    <div className={s.untertitel}>
      <span>{detail.schluessel.wert}</span>
      <CopyButton value={detail.schluessel.wert} label={detail.schluessel.feld} />
    </div>
  )

  return (
    // Der Umschlag trägt nichts zum Layout bei — das Popover ist `fixed`. Er
    // hält nur beide zusammen, damit der Auslöser kein Popover als Kind
    // bekommt: Ein Knopf im Knopf ist ungültig, und genau das steht hier drin.
    <span className={s.anker}>
      <button
        ref={anchor}
        type="button"
        className={className}
        aria-expanded={place !== null}
        aria-haspopup="dialog"
        onClick={(event) => {
          // Nicht bis zur Zeile durchreichen: Eine Tabellenzeile kann
          // anklickbar sein, und wer den Verweis aufklappt, will nicht auf
          // die Detailseite der ZEILE.
          event.stopPropagation()
          if (place) zeigen(null)
          else measure()
        }}
      >
        {children}
      </button>

      {/*
        AM `body` UND NICHT AN DIESER STELLE IM BAUM.

        `position: fixed` allein genuegt nicht: Die erste Spalte der Tabelle
        ist `position: sticky` mit `z-index: 2`, und das eroeffnet einen
        STAPELKONTEXT. Darin gilt das `z-index: 60` des Popovers nur relativ
        zu dieser einen Zelle — die Zeilen darunter, selbst auf Stufe 2, aber
        spaeter im Baum, malen darueber. Aufgefallen in der Watchlist: Das
        Popover ging auf und war nicht zu sehen. Bei den Meldungen fiel es
        nicht auf, weil die Verweisspalte dort nicht die erste ist.

        Ein Tor an den `body` hat keine solchen Vorfahren. Die Lage aendert
        sich dadurch nicht — sie ist ohnehin am Fenster gemessen.
      */}
      {place && createPortal(
        <div ref={panel} className={s.popover} role="dialog"
             aria-label={detail.name ?? detail.ref}
             // DAS TOR BRICHT DIE DOM-KETTE, NICHT DIE REACT-KETTE: Ein
             // Klick im Popover blubbert weiter zu dem, was die Zelle
             // umgibt — bei der Watchlist zur Zeile, die sich dann
             // zuklappt. Deshalb hier ein zweites Mal anhalten.
             onClick={(event) => event.stopPropagation()}
             style={{ top: place.top, bottom: place.bottom, left: place.left }}>

          <div className={s.kopf} onPointerDown={ziehen}
               title="Zum Verschieben anfassen">
            <div className={s.titel}>
              <div className={s.name}>
                {detail.name ?? <>⚠ Ziel nicht gefunden</>}
              </div>
              {untertitel}
              {!untertitel && detail.art && <div className={s.art}>{detail.art}</div>}
            </div>
            <button type="button" className={s.schliessen}
                    aria-label="Schließen" onClick={schliessen}>
              ✕
            </button>
          </div>

          {(detail.schluessel || detail.daten || detail.kurs) && (
            <div className={s.gruppe}>
              {/* Beim Instrument steht die ISIN schon im Kopf, neben dem
                  Symbol — hier stünde sie zweimal. */}
              {detail.schluessel && !untertitel && (
                <div className={s.zeile}>
                  <span className={s.feld}>{detail.schluessel.feld}</span>
                  <span className={s.wert}>{detail.schluessel.wert}</span>
                  <CopyButton value={detail.schluessel.wert}
                              label={detail.schluessel.feld} />
                </div>
              )}
              {detail.kurs && (
                <div className={s.zeile}>
                  <span className={s.feld}>Kurs</span>
                  <span className={s.wert}>{detail.kurs.betrag}</span>
                </div>
              )}
              {detail.daten?.map((d) => (
                <div key={d.feld} className={s.zeile}>
                  <span className={s.feld}>{d.feld}</span>
                  {d.wert
                    ? <span className={s.wertText}>{d.wert}</span>
                    : <span className={s.fehlt}>—</span>}
                </div>
              ))}
            </div>
          )}

          {detail.ampel && (
            <div className={s.gruppe}>
              <div className={s.zeile}>
                <span className={s.feld}>Ampel</span>
                <span className={s.ampel}>
                  <span className={`${s.punkt} ${s[detail.ampel.stufe.toLowerCase()]}`}
                        aria-hidden="true" />
                  <span className={s.stufe}>{STUFEN[detail.ampel.stufe]}</span>
                </span>
                <span className={s.seit}>{detail.ampel.seit}</span>
              </div>
              <div className={s.satz}>„{detail.ampel.satz}“</div>
            </div>
          )}

          {detail.news && (
            <div className={s.gruppe}>
              <div className={s.zeile}>
                <span className={s.feld}>News</span>
                <span className={s.wertText}>
                  {detail.news.anzahl} · zuletzt {detail.news.zuletzt}
                </span>
              </div>
              {detail.news.items.map((n) => (
                <div key={n.id} className={s.meldung}>
                  <span className={`${s.bewertung} ${ton(n.sentiment)}`}>
                    {n.sentiment > 0 ? `+${n.sentiment}` : n.sentiment}
                  </span>
                  <span className={s.ueberschrift}>{n.headline}</span>
                </div>
              ))}
            </div>
          )}

          {detail.name === null && (
            <div className={s.gruppe}>
              <div className={s.zeile}>
                <span className={s.feld}>Verweis</span>
                <span className={s.wert}>{detail.ref}</span>
                <CopyButton value={detail.ref} label="Verweis" />
              </div>
              {/* Nicht verschweigen, woran es liegt: `refs` ist ein Array, und
                  ein Array kann in PostgreSQL keinen Fremdschlüssel tragen.
                  Wer das weiß, sucht den Fehler nicht in der Anzeige. */}
              <p className={s.verwaist}>
                Den Eintrag gibt es nicht mehr. Der Verweis bleibt stehen,
                weil er sonst spurlos verschwände.
              </p>
            </div>
          )}

          {detail.aktionen && detail.aktionen.length > 0 && (
            <div className={s.fuss}>
              {detail.aktionen.map((a) => (
                <Button key={a.to} onClick={() => navigate(a.to)}>
                  {a.text} ↗
                </Button>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}

    </span>
  )
}

/** Drei Töne statt sieben: Richtung genügt, den Betrag zeigt die Zahl. */
function ton(sentiment: number): string {
  if (sentiment > 0) return s.positiv
  if (sentiment < 0) return s.negativ
  return s.neutral
}
